import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {FabricService} from 'src/app/services/fabric/fabric.service';

const SERVICE_NAME = 'mgmt-sizing-resources';

interface SizingMetric {
  label: string;
  value: any;
  overrideAllowed: boolean;
}

interface SizingSection {
  sectionTitle: string;
  metrics: SizingMetric[];
  isNested: boolean;
}

interface SizingCard {
  component: string;
  title: string;
  sections: SizingSection[];
  edited?: boolean;
}


@Injectable({
  providedIn: 'root'
})
export class SizingService {

  constructor(private fabricService: FabricService) {}

  postSizingDocs(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_instances_id_sizing-docs', obj);
  }

  getSizingDocs(id: String): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_sizing-docs', {id});
  }

  getSizingDocsById(id: String, sizingDocumentId: String): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_sizing-docs_sizingdocumentid', {id, sizingDocumentId});
  }

  getSizingTemplate(id?: String, environmentSize?: String, deploymentClass?: String): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_sizing-templates', {id, environmentSize, deploymentClass});
  }

  getSizingTemplateById(id: String): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_sizing-templates_id', {id});
  }

  deleteSizingDocsBySizingId(id: String, sizingDocumentId: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_mgmt_instances_id_sizing-docs_sizingdocumentid', {id, sizingDocumentId});
  }

  putSizingDocsById(id: string, sizingDocumentId: string, obj: any): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_sizing-docs_sizingdocumentid', {id, sizingDocumentId, ...obj});
  }

  getSizingTemplateByInstanceId(id: String, blueprintVersionId?: string, environmentSize?: String): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_instances_id_sizing-templates', {id, blueprintVersionId, ...(environmentSize ? { environmentSize } : {})});
  }

  applySizingDocId(id: String, sizingDocumentId: string, ticketNumber?: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_instances_id_resize', {id, sizingDocumentId, ticketNumber});
  }

  /**
   * Transforms raw sizing template records into structured sizing cards
   * @param records Raw sizing template data from API
   * @returns Array of sizing card objects ready for display
   */
  
  buildSizingCards(record: any): SizingCard  {
    const template = record?.template;
    if (!template?.properties) return null as any;
 
    const rootProperties = template.properties;
    const sections: SizingSection[] = [];
 
    const hasNestedSections = Object.values(rootProperties).some(
      (prop: any) => prop?.type === 'object' && prop?.properties
    );
 
    if (hasNestedSections) {
 
      Object.keys(rootProperties).forEach(sectionKey => {
 
        const sectionSchema = rootProperties[sectionKey];
 
        if (!sectionSchema?.properties) return;
 
        const metrics: SizingMetric[] = Object.keys(sectionSchema.properties).map(metricKey => {
 
          const prop = sectionSchema.properties[metricKey];
 
          return {
            key: metricKey,
            label: prop?.title || this.formatLabel(metricKey),
            value: prop?.default,
            type: prop?.type,
            overrideAllowed: prop?.['x-override-allowed'] ?? false
          };
 
        });
 
        sections.push({
          sectionTitle: sectionSchema.title || this.formatLabel(sectionKey),
          metrics,
          isNested: true
        });
 
      });
 
    }
 
    else {
 
      const metrics: SizingMetric[] = Object.keys(rootProperties).map(metricKey => {
 
        const prop = rootProperties[metricKey];
 
        return {
          key: metricKey,
          label: prop?.title || this.formatLabel(metricKey),
          value: prop?.default,
          type: prop?.type,
          overrideAllowed: prop?.['x-override-allowed'] ?? false
        };
 
      });
 
      sections.push({
        sectionTitle: template.title || record.component,
        metrics,
        isNested: false
      });
 
    }
 
    return {
      component: record.component,
      title: template.title,
      sections
    };
}

  /**
   * Formats a property key into a readable label
   * @param key Property key to format
   * @returns Formatted label string
   */
  private formatLabel(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

private sizingDocumentId: string | null = null;

  setSizingDocumentId(id: string): void {
    this.sizingDocumentId = id;
  }

  getSizingDocumentId(): string | null {
    return this.sizingDocumentId;
  }

  private schemaTypeMap: Record<string, string> = {};

  //Build schema map from template API
  buildSchemaMap(records: any[]): void {
    this.schemaTypeMap = {};

    records.forEach(record => {
      const component = record.component;
      const template = record.template;

      if (!template?.properties) return;

      Object.keys(template.properties).forEach(sectionKey => {
        const section = template.properties[sectionKey];

        if (!section?.properties) return;

        Object.keys(section.properties).forEach(metricKey => {
          const prop = section.properties[metricKey];

          const key = this.getKey(component, sectionKey, metricKey);

          this.schemaTypeMap[key] = prop.type;
        });
      });
    });
  }

  //Get type
  getType(component: string, section: string, metric: string): string {
    return this.schemaTypeMap[this.getKey(component, section, metric)];
  }

  //Internal key generator
  private getKey(component: string, section: string, metric: string): string {
    return `${component}.${section}.${metric}`;
  }

}
